import { concat, Dictionary, fromPairs, map, reduce } from 'lodash';
import { Injectable } from '@angular/core';

import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { Observable, rx } from '@app/shared/rx';
import {
  IBaseEntityResponse,
  IBaseEntityType,
} from '@app/shared/types/entities/base/base.type';
import { MAX_PAGE_SIZE } from '@config/global-constants';

import { DmDataService } from '../../dm-data/dm-data.service';
import { TransferService } from '../../transfer/transfer.service';
import { IReplenishPercentMap } from '../replenish.types';
import { getPendingQuantityMap, isSkipReplenish } from '../replenish.utils';
import { ReplenishDataSource } from './replenish-data.enums';
import { AbstractReplenishDataService } from './abstract-replenish-data.service';

@Injectable()
class ReplenishPendingQtyDataService extends AbstractReplenishDataService<
  Dictionary<IBaseEntityType>
> {
  public static SOURCE_NAME: string = ReplenishDataSource.PendingQty;

  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;

  public fetch(params: {
    percentDesMap: IReplenishPercentMap;
  }): Observable<Dictionary<IBaseEntityType>> {
    const needFetchSites = reduce(
      params.percentDesMap,
      (sites, destination) => {
        if (isSkipReplenish(destination) || destination.replenished) {
          return sites;
        }

        return concat(sites, [
          {
            fromSite: destination.fromSite,
            toSite: destination.toSite,
          },
        ]);
      },
      [],
    );

    return this.dmDataService
      .getSiteGroupId(map(needFetchSites, 'toSite'))
      .pipe(
        rx.mergeMap((groupId: string) =>
          this.transferService.batchFetchList(
            {
              filter: `status eq 'pending' and (site_id eq in({siteGroup}) or site_id_to eq in({siteGroup}))`,
              select: [
                'site_id',
                'site_id_to',
                'inventory_items.item_entity_id',
                'inventory_items.initial_count',
                'inventory_items.measure_factor_value',
              ],
              top: MAX_PAGE_SIZE,
            },
            groupId,
          ),
        ),
      )
      .pipe(
        rx.map((transferResponse: IBaseEntityResponse) => {
          return fromPairs(
            map(needFetchSites, (site) => {
              return [
                site.toSite,
                getPendingQuantityMap(
                  transferResponse,
                  site.fromSite,
                  site.toSite,
                ),
              ];
            }),
          );
        }),
      );
  }
}

export { ReplenishPendingQtyDataService };
